'use client';

import { useDarkMode } from '@/lib/platform/contexts/dark-mode-context';
import { ALL_COUNTRIES, EU_COUNTRIES } from '@/lib/platform/data/countries';
import { Listbox, ListboxButton, ListboxOption, ListboxOptions } from '@headlessui/react';
import { CheckIcon, ChevronUpDownIcon } from '@heroicons/react/20/solid';
import { checkVAT, countries } from 'jsvat';

interface VatInputProps {
  label?: string;
  vatCountry: string;
  value: string;
  onVatCountryChange: (country: string) => void;
  onValueChange: (value: string) => void;
  error?: string;
  helperText?: string;
  required?: boolean;
  disabled?: boolean;
}

export function VatInput({
  label,
  vatCountry,
  value,
  onVatCountryChange,
  onValueChange,
  error,
  helperText,
  required,
  disabled,
}: VatInputProps) {
  const { darkMode } = useDarkMode();

  // Sort countries alphabetically by name
  const sortedCountries = [...ALL_COUNTRIES].sort((a, b) => a.name.en.localeCompare(b.name.en));

  // Find the selected country
  const selectedCountry = sortedCountries.find((c) => c.code === vatCountry) || sortedCountries[0];

  // Check if selected country is EU
  const isEUCountry = EU_COUNTRIES.some((c) => c.code === selectedCountry.code);

  // Validate VAT number format using jsvat (only for EU countries)
  const validateVAT = (vatNumber: string): string | undefined => {
    if (!isEUCountry) return undefined; // Non-EU countries don't need VAT validation
    if (!vatNumber) return undefined; // Empty is OK (optional field)

    // Construct full VAT with country prefix
    const fullVAT = `${selectedCountry.vatPrefix}${vatNumber}`;

    // Check VAT format
    const result = checkVAT(fullVAT, countries);

    if (!result.isValid) {
      return `Invalid ${selectedCountry.vatPrefix} VAT format`;
    }

    return undefined;
  };

  // Handle value change with validation
  const handleValueChange = (newValue: string) => {
    // Remove all whitespace (spaces, tabs, newlines, etc.)
    const sanitizedValue = newValue.replace(/\s/g, '');
    onValueChange(sanitizedValue);
  };

  // Calculate validation error (if not provided externally)
  // Only validate for EU countries
  const validationError = isEUCountry ? error || validateVAT(value) : undefined;

  return (
    <div>
      {label && (
        <label
          className={`mb-2 block text-[13px] font-medium ${
            darkMode ? 'text-gray-400' : 'text-gray-600'
          }`}
        >
          {label}
          {required && <span className="ml-1 text-red-500">*</span>}
        </label>
      )}
      <div>
        <div
          className={`flex ${isEUCountry ? 'rounded-md' : 'rounded-md'} outline outline-1 -outline-offset-1 ${
            validationError
              ? darkMode
                ? 'outline-red-400'
                : 'outline-red-600'
              : darkMode
                ? 'outline-white/10 has-[button:focus]:outline-2 has-[button:focus]:-outline-offset-2 has-[button:focus]:outline-indigo-500 has-[input:focus]:outline-2 has-[input:focus]:-outline-offset-2 has-[input:focus]:outline-indigo-500'
                : 'outline-gray-300 has-[button:focus]:outline-2 has-[button:focus]:-outline-offset-2 has-[button:focus]:outline-indigo-600 has-[input:focus]:outline-2 has-[input:focus]:-outline-offset-2 has-[input:focus]:outline-indigo-600'
          } ${darkMode ? 'bg-white/5' : 'bg-white'}`}
        >
          <Listbox as="div" value={vatCountry} onChange={onVatCountryChange} disabled={disabled}>
            <div className={`relative ${isEUCountry ? 'shrink-0' : 'grow'}`}>
              <ListboxButton
                aria-label="Country"
                className={`flex w-full cursor-default appearance-none items-center justify-between gap-1 ${isEUCountry ? 'rounded-l-md' : 'rounded-md'} py-1.5 pr-8 pl-3 text-left text-base focus:outline-none sm:text-sm/6 ${
                  darkMode ? 'bg-transparent text-gray-100' : 'bg-transparent text-gray-900'
                } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
              >
                <span>
                  {isEUCountry
                    ? selectedCountry?.vatPrefix
                    : `${selectedCountry?.flag} ${selectedCountry?.name.en}`}
                </span>
                <ChevronUpDownIcon
                  aria-hidden="true"
                  className={`size-4 shrink-0 ${darkMode ? 'text-gray-400' : 'text-gray-500'}`}
                />
              </ListboxButton>

              <ListboxOptions
                transition
                className={`absolute z-10 mt-1 max-h-56 w-64 overflow-auto rounded-md py-1 text-base shadow-lg ring-1 transition duration-100 ease-in focus:outline-hidden data-closed:opacity-0 sm:text-sm ${
                  darkMode ? 'bg-gray-800 ring-white/10' : 'bg-white ring-black/5'
                } `}
              >
                {sortedCountries.map((country) => (
                  <ListboxOption
                    key={country.code}
                    value={country.code}
                    className={`group relative cursor-default py-2 pr-9 pl-3 select-none data-focus:outline-hidden ${
                      darkMode
                        ? 'text-gray-100 data-focus:bg-indigo-600 data-focus:text-white'
                        : 'text-gray-900 data-focus:bg-indigo-600 data-focus:text-white'
                    } `}
                  >
                    <div className="flex items-center justify-between gap-2">
                      <span className="block truncate font-normal group-data-selected:font-semibold">
                        {country.flag} {country.name.en}
                      </span>
                    </div>

                    <span className="absolute inset-y-0 right-0 flex items-center pr-4 text-indigo-600 group-not-data-selected:hidden group-data-focus:text-white">
                      <CheckIcon aria-hidden="true" className="size-5" />
                    </span>
                  </ListboxOption>
                ))}
              </ListboxOptions>
            </div>
          </Listbox>

          {isEUCountry && (
            <input
              type="text"
              value={value}
              onChange={(e) => handleValueChange(e.target.value)}
              disabled={disabled}
              placeholder="Enter VAT number"
              className={`block min-w-0 grow py-1.5 pr-4 pl-2 text-base focus:outline-none sm:text-sm/6 ${
                darkMode
                  ? 'bg-transparent text-gray-100 placeholder:text-gray-500'
                  : 'bg-transparent text-gray-900 placeholder:text-gray-400'
              } ${disabled ? 'cursor-not-allowed opacity-50' : ''}`}
            />
          )}
        </div>
        {validationError && (
          <p className={`mt-2 text-sm ${darkMode ? 'text-red-400' : 'text-red-600'}`}>
            {validationError}
          </p>
        )}
        {helperText && !validationError && (
          <p className={`mt-2 text-sm ${darkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            {helperText}
          </p>
        )}
      </div>
    </div>
  );
}
